import { forwardRef, type ForwardedRef, type HTMLAttributes } from 'react';

import { cn } from '../../lib/cn';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

const EM_DASH = '—';

interface DashProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * What the dash means, already translated — "Sans heures", "Aucune sortie",
   * "Pas encore calculé". Required: a bare dash is not information, and a
   * screen reader announces it as nothing at all.
   */
  readonly label: string;
  /** Show the word beside the dash. Off in a dense grid cell, on in a total. */
  readonly showLabel?: boolean;
}

export type NullValueProps = DashProps;
export type NoValueProps = DashProps;
export type PendingValueProps = DashProps;

function Dash({
  label,
  showLabel = false,
  className,
  toneClass,
  forwardedRef,
  ...rest
}: DashProps & {
  readonly toneClass: string;
  readonly forwardedRef: ForwardedRef<HTMLSpanElement>;
}) {
  return (
    <span
      {...rest}
      ref={forwardedRef}
      className={cn('num inline-flex items-baseline gap-1', toneClass, className)}
    >
      <span aria-hidden="true">{EM_DASH}</span>
      {showLabel ? (
        <span className="font-sans text-body-compact font-normal">{label}</span>
      ) : (
        <VisuallyHidden>{label}</VisuallyHidden>
      )}
    </span>
  );
}

/**
 * A payroll number that is MISSING — an hours total with no punch-out, an
 * effective time that cannot be computed.
 *
 * `0 h 00` is a lie: it says "this person worked no hours" when the truth is
 * "nobody knows how long this person worked". So it renders as an em-dash in
 * danger ink at weight 700, and a total that includes one says how many rows it
 * could not count ("96 h 58 · 2 sans heures") rather than folding a null into a
 * sum. This is the single most payroll-critical glyph in the product; the
 * direction that rendered it at 3.35:1 lost points for exactly that.
 */
export const NullValue = forwardRef<HTMLSpanElement, NullValueProps>(function NullValue(
  props,
  ref,
) {
  return <Dash {...props} forwardedRef={ref} toneClass="font-bold text-status-danger-ink" />;
});

/**
 * A value that is legitimately absent — no zone on this site, no second
 * adjustment, no note.
 *
 * Nothing is wrong, so nothing is red. Muted ink, normal weight. Using
 * `NullValue` here is the mistake that makes a grid look like it is on fire.
 */
export const NoValue = forwardRef<HTMLSpanElement, NoValueProps>(function NoValue(props, ref) {
  return <Dash {...props} forwardedRef={ref} toneClass="text-ink-muted" />;
});

/**
 * A value that does not exist YET — hours for a shift that has not ended, a
 * total for a period still open.
 *
 * Distinct from `NoValue` because the answer changes later, and distinct from
 * `NullValue` because nobody needs to act. Muted ink, and the label is what
 * carries the difference ("Pas encore calculé").
 */
export const PendingValue = forwardRef<HTMLSpanElement, PendingValueProps>(function PendingValue(
  props,
  ref,
) {
  return <Dash {...props} forwardedRef={ref} toneClass="text-ink-muted italic" />;
});
